import { environment } from 'src/environments/environment';
import type { SearchResult } from './search.service';

export type SearchScope = 'public' | 'admin';

export type SearchEntityType = 'JOB' | 'COMPANY' | 'USER' | 'UNKNOWN';

export function getSearchEntityType(result: SearchResult): SearchEntityType {
  const candidates = [
    result.type,
    result.entityType,
    result.sourceType,
    result.module,
    result.category,
  ]
    .filter(Boolean)
    .map((value) => String(value).toUpperCase());

  if (candidates.some((value) => value.includes('JOB') || value.includes('POSITION') || value.includes('VACANCY'))) {
    return 'JOB';
  }

  if (candidates.some((value) => value === 'JOB_POST' || value.includes('JOB_POST'))) {
    return 'JOB';
  }

  if (candidates.some((value) => value.includes('COMPANY') || value.includes('ORG'))) {
    return 'COMPANY';
  }

  if (candidates.some((value) => value.includes('USER') || value.includes('PROFILE') || value.includes('CANDIDATE'))) {
    return 'USER';
  }

  if (result.jobTitle || result.positionTitle || result.department || result.jobPosition) {
    return 'JOB';
  }

  if (result.companyId || result.companyName || result.company || result.companyLogoUrl || result.logoUrl) {
    return 'COMPANY';
  }

  return 'UNKNOWN';
}

export function getSearchPrimaryLabel(result: SearchResult): string {
  return (
    result.name ||
    result.title ||
    result.jobTitle ||
    result.positionTitle ||
    result.jobPosition ||
    result.companyName ||
    result.company ||
    'Untitled'
  );
}

export function getSearchSecondaryLabel(result: SearchResult): string {
  const entityType = getSearchEntityType(result);

  if (entityType === 'JOB') {
    return (
      result.companyName ||
      result.company ||
      result.department ||
      result.category ||
      result.sourceLabel ||
      result.locationType ||
      result.type ||
      ''
    );
  }

  if (entityType === 'COMPANY') {
    return (
      result.industryType ||
      result.department ||
      result.category ||
      result.sourceLabel ||
      result.type ||
      'Company'
    );
  }

  if (entityType === 'USER') {
    return result.sourceLabel || result.department || result.category || result.type || 'User';
  }

  return (
    result.sourceLabel ||
    result.department ||
    result.category ||
    result.type ||
    result.entityType ||
    result.sourceType ||
    ''
  );
}

export function getSearchBadgeLabel(result: SearchResult): string {
  const entityType = getSearchEntityType(result);
  if (entityType !== 'UNKNOWN') {
    return entityType;
  }

  return (
    result.type ||
    result.entityType ||
    result.sourceType ||
    result.category ||
    result.department ||
    'Result'
  ).toString();
}

export function getSearchAvatarLabel(result: SearchResult): string {
  const companyLabel = result.companyName || result.company || '';

  if (!companyLabel && (result.confidential || getSearchEntityType(result) === 'COMPANY')) {
    return 'Confidential';
  }

  if (companyLabel) {
    return companyLabel.trim().charAt(0).toUpperCase() || 'C';
  }

  const primary = getSearchPrimaryLabel(result);
  return primary.trim().charAt(0).toUpperCase() || 'R';
}

export function getSearchImageUrl(result: SearchResult): string {
  const rawUrl =
    result.companyLogoUrl ||
    result.logoUrl ||
    result.logo ||
    result.imageUrl ||
    '';

  if (!rawUrl) {
    return '';
  }

  const normalized = normalizeSearchImageUrl(String(rawUrl));

  if (
    normalized.startsWith('http://') ||
    normalized.startsWith('https://') ||
    normalized.startsWith('data:') ||
    normalized.startsWith('blob:')
  ) {
    return normalized;
  }

  if (environment.apiImagePath && normalized.startsWith(environment.apiImagePath)) {
    return normalizeSearchImageUrl(normalized);
  }

  if (environment.apiImagePath) {
    if (normalized.startsWith('/assets/') || normalized.startsWith('assets/')) {
      return normalizeSearchImageUrl(
        `${environment.apiImagePath.replace(/\/+$/, '')}/${normalized.replace(/^\/+/, '')}`
      );
    }

    return normalizeSearchImageUrl(
      `${environment.apiImagePath.replace(/\/+$/, '')}/${normalized.replace(/^\/+/, '')}`
    );
  }

  return normalizeSearchImageUrl(normalized);
}

export function hasSearchImage(result: SearchResult): boolean {
  return !!getSearchImageUrl(result);
}

function normalizeSearchImageUrl(url: string): string {
  const cleaned = String(url).trim().replace(/\\/g, '/');
  return cleaned
    .replace(/([^:]\/)\/+/g, '$1')
    .replace(/^(https?:)\/([^/])/, '$1//$2');
}
